/**
 * Query key factory for consistent cache management.
 * Keys are hierarchical to enable granular invalidation.
 * Pattern: ['domain', 'type', params]
 */

import type { ClientListParams } from "@/apis/client/client-apis";
import type { RentalListQueryParams } from "@/apis/rental/rental-apis";
import type { GetReservationsByFleetParams } from "@/apis/reservations/apis";
import type { StationListParams } from "@/apis/station/station-apis";
import type { VehicleListParams } from "@/apis/vehicle/vehicle-apis";
import type { RentalListParams } from "@/domain/rental";
import type { ListParams } from "./types";

export const queryKeys = {
  analytics: {
    all: ["analytics"] as const,
    dashboard: (whitelabelId: number | null) =>
      [...queryKeys.analytics.all, "dashboard", whitelabelId] as const,
  },

  serviceAccounts: {
    all: ["serviceAccounts"] as const,
    list: () => [...queryKeys.serviceAccounts.all, "list"] as const,
  },

  intros: {
    all: ["intros"] as const,
    list: (cityId: number) =>
      [...queryKeys.intros.all, "list", cityId] as const,
  },

  warnings: {
    all: ["warnings"] as const,
    list: (cityId: number) =>
      [...queryKeys.warnings.all, "list", cityId] as const,
  },

  vehicles: {
    all: ["vehicles"] as const,
    list: (params: VehicleListParams) =>
      [...queryKeys.vehicles.all, "list", params] as const,
    detail: (id: string | number) =>
      [...queryKeys.vehicles.all, "detail", id] as const,
    history: (
      vehicleCode: string,
      startDate: string,
      endDate: string,
      page: number
    ) =>
      [
        ...queryKeys.vehicles.all,
        "history",
        vehicleCode,
        startDate,
        endDate,
        page,
      ] as const,
  },

  stations: {
    all: ["stations"] as const,
    list: (params: StationListParams) =>
      [...queryKeys.stations.all, "list", params] as const,
    detail: (id: string | number) =>
      [...queryKeys.stations.all, "detail", id] as const,
    speedLimitZones: (params: StationListParams) =>
      [...queryKeys.stations.all, "speedLimitZones", params] as const,
    speedLimitZoneDetail: (id: string | number) =>
      [...queryKeys.stations.all, "speedLimitZone", id] as const,
  },

  docks: {
    all: ["docks"] as const,
    list: (params: ListParams) =>
      [...queryKeys.docks.all, "list", params] as const,
  },

  locks: {
    all: ["locks"] as const,
    list: (params: ListParams) =>
      [...queryKeys.locks.all, "list", params] as const,
  },

  adminUsers: {
    all: ["adminUsers"] as const,
    list: (params: ListParams) =>
      [...queryKeys.adminUsers.all, "list", params] as const,
    detail: (email: string) =>
      [...queryKeys.adminUsers.all, "detail", email] as const,
  },

  rentals: {
    all: ["rentals"] as const,
    list: (params: RentalListQueryParams) =>
      [...queryKeys.rentals.all, "list", params] as const,
    detail: (id: string | number) =>
      [...queryKeys.rentals.all, "detail", id] as const,
    gallery: (params: RentalListParams) =>
      [...queryKeys.rentals.all, "gallery", params] as const,
    userSearch: (search: string) =>
      [...queryKeys.rentals.all, "userSearch", search] as const,
  },

  transactions: {
    all: ["transactions"] as const,
    list: (params: ListParams) =>
      [...queryKeys.transactions.all, "list", params] as const,
    detail: (id: string) =>
      [...queryKeys.transactions.all, "detail", id] as const,
  },

  referrals: {
    all: ["referrals"] as const,
    list: (params: ListParams) =>
      [...queryKeys.referrals.all, "list", params] as const,
  },

  coupons: {
    all: ["coupons"] as const,
    list: (params: ListParams) =>
      [...queryKeys.coupons.all, "list", params] as const,
    detail: (id: string | number) =>
      [...queryKeys.coupons.all, "detail", id] as const,
  },

  couponUsers: {
    all: ["couponUsers"] as const,
    list: (couponId: string, params: ListParams) =>
      [...queryKeys.couponUsers.all, "list", couponId, params] as const,
  },

  cities: {
    all: ["cities"] as const,
    list: (params: ListParams) =>
      [...queryKeys.cities.all, "list", params] as const,
    detail: (id: string | number) =>
      [...queryKeys.cities.all, "detail", id] as const,
    pricingPlans: (cityId: string | number) =>
      [...queryKeys.cities.all, "pricingPlans", cityId] as const,
    pricingPlanFields: () =>
      [...queryKeys.cities.all, "pricingPlanFields"] as const,
    galleryPricingPlans: (cityId: number) =>
      [...queryKeys.cities.all, "galleryPricingPlans", cityId] as const,
    pricingPlanDetail: (planId: number, isDraft: boolean) =>
      [...queryKeys.cities.all, "pricingPlanDetail", planId, isDraft] as const,
    whitelabels: () => [...queryKeys.cities.all, "whitelabels"] as const,
  },

  clients: {
    all: ["clients"] as const,
    list: (params: ClientListParams) =>
      [...queryKeys.clients.all, "list", params] as const,
    detail: (id: string | number) =>
      [...queryKeys.clients.all, "detail", id] as const,
  },

  map: {
    all: ["map"] as const,
    data: (paramsStr: string) =>
      [...queryKeys.map.all, "data", paramsStr] as const,
  },

  enums: {
    bikeStatuses: ["enums", "bikeStatuses"] as const,
    bikeBrands: ["enums", "bikeBrands"] as const,
    bikeLockStatuses: ["enums", "bikeLockStatuses"] as const,
    stationStatuses: ["enums", "stationStatuses"] as const,
    vehicleStatuses: ["enums", "vehicleStatuses"] as const,
    stationVehicleStatuses: ["enums", "stationVehicleStatuses"] as const,
    vehicleBrands: ["enums", "vehicleBrands"] as const,
    lockStatuses: ["enums", "lockStatuses"] as const,
    vehicleTypes: ["enums", "vehicleTypes"] as const,
    rentalStatuses: ["enums", "rentalStatuses"] as const,
    dockStatuses: ["enums", "dockStatuses"] as const,
    currencyMappings: ["enums", "currencyMappings"] as const,
    planTypes: ["enums", "planTypes"] as const,
    planStatuses: ["enums", "planStatuses"] as const,
  },

  fleets: {
    all: ["fleets"] as const,
    byPaymentConfig: () =>
      [...queryKeys.fleets.all, "byPaymentConfig"] as const,
    byCity: (cityId: number) =>
      [...queryKeys.fleets.all, "city", cityId] as const,
  },

  userTags: {
    all: ["userTags"] as const,
    byLegacyIds: (legacyIds: string[]) =>
      [...queryKeys.userTags.all, "byLegacyIds", legacyIds] as const,
  },

  wallets: {
    all: ["wallets"] as const,
    byLegacyUser: (userId: number, cityId?: number) =>
      [...queryKeys.wallets.all, "byLegacyUser", userId, cityId] as const,
  },

  customers: {
    all: ["customers"] as const,
    blockStatus: (userId: string) =>
      [...queryKeys.customers.all, "blockStatus", userId] as const,
  },

  users: {
    all: ["users"] as const,
    detail: (userId: number) =>
      [...queryKeys.users.all, "detail", userId] as const,
  },

  reservations: {
    all: ["reservations"] as const,
    list: (params: GetReservationsByFleetParams) =>
      [...queryKeys.reservations.all, "list", params] as const,
    vehiclesList: () =>
      [...queryKeys.reservations.all, "vehiclesList"] as const,
    fleetByCity: (cityId: number) =>
      [...queryKeys.reservations.all, "fleetByCity", cityId] as const,
    configByCity: (cityId: number) =>
      [...queryKeys.reservations.all, "configByCity", cityId] as const,
    configByFleet: (fleetId: string) =>
      [...queryKeys.reservations.all, "configByFleet", fleetId] as const,
  },

  rewards: {
    all: ["rewards"] as const,
    programs: (params?: { page?: number; size?: number }) =>
      [...queryKeys.rewards.all, "programs", params] as const,
    claims: (params?: { page?: number; size?: number }) =>
      [...queryKeys.rewards.all, "claims", params] as const,
    rules: (params?: { programIds?: (string | number)[] }) =>
      [...queryKeys.rewards.all, "rules", params] as const,
  },

  ridePasses: {
    all: ["ridePasses"] as const,
    list: (fleetId?: string) =>
      [...queryKeys.ridePasses.all, "list", fleetId] as const,
    detail: (id: string) =>
      [...queryKeys.ridePasses.all, "detail", id] as const,
  },

  serviceHealth: {
    all: ["serviceHealth"] as const,
    list: () => [...queryKeys.serviceHealth.all, "list"] as const,
    history: (serviceId: string) =>
      [...queryKeys.serviceHealth.all, "history", serviceId] as const,
  },

  appConfiguration: {
    all: ["appConfiguration"] as const,
    org: (orgId: string) =>
      [...queryKeys.appConfiguration.all, "org", orgId] as const,
    city: (cityId: number) =>
      [...queryKeys.appConfiguration.all, "city", cityId] as const,
  },

  organizations: {
    all: ["organizations"] as const,
    fleetByCity: (cityId: number) =>
      [...queryKeys.organizations.all, "fleetByCity", cityId] as const,
    fleets: (organizationId: string) =>
      [...queryKeys.organizations.all, "fleets", organizationId] as const,
    paymentConfigs: (organizationId: string) =>
      [
        ...queryKeys.organizations.all,
        "paymentConfigs",
        organizationId,
      ] as const,
  },

  developerFeatureFlags: {
    all: ["developerFeatureFlags"] as const,
    org: (orgId: string) =>
      [...queryKeys.developerFeatureFlags.all, "org", orgId] as const,
  },

  partners: {
    all: ["partners"] as const,
    list: (whitelabelId: number | null) =>
      [...queryKeys.partners.all, "list", whitelabelId] as const,
  },

  vehicleModels: {
    all: ["vehicleModels"] as const,
    list: (organizationId: string) =>
      [...queryKeys.vehicleModels.all, "list", organizationId] as const,
    // Keyed on the model, not the page: the continuation token is carried inside
    // the infinite query rather than in the key, so paging further never
    // invalidates the pages already held.
    assignments: (organizationId: string, vehicleModelId: string) =>
      [
        ...queryKeys.vehicleModels.all,
        "assignments",
        organizationId,
        vehicleModelId,
      ] as const,
    allAssignments: (organizationId: string, vehicleModelId: string) =>
      [
        ...queryKeys.vehicleModels.all,
        "allAssignments",
        organizationId,
        vehicleModelId,
      ] as const,
    // One entry per page of the bikes table. `vehicleCodes` is joined rather than
    // passed as an array so two renders of the same page share a cache entry.
    forVehicles: (organizationId: string, vehicleCodes: string[]) =>
      [
        ...queryKeys.vehicleModels.all,
        "forVehicles",
        organizationId,
        vehicleCodes.join(","),
      ] as const,
  },

  modelPricing: {
    all: ["modelPricing"] as const,
    revisions: (organizationId: string, currency?: string) =>
      [
        ...queryKeys.modelPricing.all,
        "revisions",
        organizationId,
        currency ?? "all",
      ] as const,
    assignments: (organizationId: string, vehicleModelId: string) =>
      [
        ...queryKeys.modelPricing.all,
        "assignments",
        organizationId,
        vehicleModelId,
      ] as const,
  },

  fines: {
    all: ["fines"] as const,
    // One entry per (organization, rental) and per (organization, rider): the
    // two surfaces that link into the fines page, and the two filters the
    // gateway actually accepts.
    forRental: (organizationId: string, journeyId: string) =>
      [...queryKeys.fines.all, "forRental", organizationId, journeyId] as const,
    forUser: (organizationId: string, riderId: string) =>
      [...queryKeys.fines.all, "forUser", organizationId, riderId] as const,
    list: (organizationId: string) =>
      [...queryKeys.fines.all, "list", organizationId] as const,
    // The audit trail: fine, attempts, notices and transitions in one response.
    detail: (organizationId: string, fineId: string) =>
      [...queryKeys.fines.all, "detail", organizationId, fineId] as const,
  },

  partnerAccess: {
    all: ["partnerAccess"] as const,
    links: (partnerId: number) =>
      [...queryKeys.partnerAccess.all, "links", partnerId] as const,
    accessLog: (partnerId: number, linkId: number) =>
      [...queryKeys.partnerAccess.all, "accessLog", partnerId, linkId] as const,
  },
};
